import { IconNavigationHomeOutline } from "@/assets/icons/figma/IconNavigationHomeOutline";
import { useAuth } from "@/auth/hooks";
import type { AccountMenuItem } from "@/common/contracts/account.contracts";
import { ACCOUNT_FAVORITES_ROUTE } from "@/common/static/routes.static";
import { openHomeFetch, retryServerErrors } from "@/features/open-home/client";
import { useOpenHomeGate } from "@/features/open-home/gate";
import type { OpenHomeSummary } from "@/features/open-home/server/summary";
import { useQuery } from "@tanstack/react-query";

export const OPEN_HOME_MY_HOME_PATH = "/open-home/my-home";
/** Bids on the member's pieces, answered in My Open Home (the Open Home bids page is gone). */
export const OPEN_HOME_SELLER_OFFERS_ID = "open-home-offers";
export const OPEN_HOME_OFFERS_PATH = `${OPEN_HOME_MY_HOME_PATH}#${OPEN_HOME_SELLER_OFFERS_ID}`;
/** Saved rooms: the Rooms tab of the account Favorites. */
export const OPEN_HOME_SAVED_ROOMS_PATH = `${ACCOUNT_FAVORITES_ROUTE}?tab=rooms`;

/**
 * The member's Open Home at a glance, for the account menu and dashboard. Fail-soft: any error
 * (no store, gateway without Open Home, signed out) is `null`, and the callers render nothing.
 */
export const useOpenHomeSummary = () => {
  const { token } = useAuth();
  // Behind the `open_home` gate: off means no request and no entry point at all.
  const on = useOpenHomeGate();
  const query = useQuery({
    queryKey: ["open-home", "summary", Boolean(token)],
    enabled: Boolean(token) && on,
    // A gateway hiccup used to hide "My Open Home" from the account menu until the next page.
    retry: retryServerErrors,
    staleTime: 60_000,
    queryFn: () =>
      openHomeFetch<{ summary: OpenHomeSummary }>(token, "summary"),
  });

  return {
    summary: !on || query.isError ? null : query.data?.summary ?? null,
    loading: on && query.isPending && Boolean(token),
  };
};

export const OPEN_HOME_MENU_ITEM: AccountMenuItem = {
  icon: IconNavigationHomeOutline,
  label: "open-home:oh-account-menu",
  labelFallback: "My Open Home",
  path: OPEN_HOME_MY_HOME_PATH,
};

/** The "My Open Home" menu entry with its offer count, or null without a room. */
export const useOpenHomeMenuEntry = () => {
  const { summary } = useOpenHomeSummary();

  return summary && summary.rooms > 0
    ? { item: OPEN_HOME_MENU_ITEM, count: summary.offersWaiting }
    : null;
};

export type OpenHomeHeadline = {
  key: string;
  fallback: string;
  count: number;
  bodyKey: string;
  bodyFallback: string;
};

const pick = (
  count: number,
  one: [string, string],
  many: [string, string]
): Pick<OpenHomeHeadline, "key" | "fallback" | "count"> =>
  count === 1
    ? { key: one[0], fallback: one[1], count }
    : { key: many[0], fallback: many[1], count };

/** Offers first, then a live home, then the review still to do. */
export const openHomeHeadline = (
  summary: OpenHomeSummary
): OpenHomeHeadline => {
  if (summary.offersWaiting > 0) {
    return {
      ...pick(
        summary.offersWaiting,
        ["open-home:oh-dash-offers-one", "1 offer is waiting in your home"],
        [
          "open-home:oh-dash-offers-many",
          "{{count}} offers are waiting in your home",
        ]
      ),
      bodyKey: "open-home:oh-dash-offers-body",
      bodyFallback:
        "Buyers made offers on pieces in your rooms. Accept, decline or let them wait.",
    };
  }

  if (summary.published && summary.piecesForSale > 0) {
    return {
      ...pick(
        summary.piecesForSale,
        ["open-home:oh-dash-live-one", "Your home is live, 1 piece for sale"],
        [
          "open-home:oh-dash-live-many",
          "Your home is live, {{count}} pieces for sale",
        ]
      ),
      bodyKey: "open-home:oh-dash-live-body",
      bodyFallback: "Members can browse your rooms and make offers.",
    };
  }

  return {
    ...pick(
      summary.piecesToReview,
      ["open-home:oh-dash-review-one", "Finish your room: 1 piece to review"],
      [
        "open-home:oh-dash-review-many",
        "Finish your room: {{count}} pieces to review",
      ]
    ),
    bodyKey: "open-home:oh-dash-review-body",
    bodyFallback:
      "Approve what is for sale, switch off what you keep, then publish.",
  };
};
